import { useState } from 'react';
import { Outlet, useNavigate, useLocation, useMatch } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  AppBar,
  Box,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Toolbar,
  Typography,
  Avatar,
  Menu,
  MenuItem,
  Divider,
  Chip,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  Menu as MenuIcon,
  HomeOutlined as HomeIcon,
  GroupsOutlined as GroupsIcon,
  ArticleOutlined as ArticleIcon,
  MenuBookOutlined as DocsIcon,
  TuneOutlined as AdminIcon,
  Logout as LogoutIcon,
  LightMode as LightModeIcon,
  DarkMode as DarkModeIcon,
} from '@mui/icons-material';
import { useAuth } from '@/context/AuthContext';
import { useThemeMode } from '@/context/ThemeContext';
import { teamApi } from '@/services/api';
import Logo from './Logo';
import FeedbackFab from './FeedbackFab';

const drawerWidth = 240;
const SIDEBAR_STORAGE_KEY = 'sidebar-open';

interface NavItem {
  text: string;
  icon: React.ReactNode;
  path: string;
  adminOnly?: boolean;
}

// Version from environment or default
const APP_VERSION = import.meta.env.VITE_APP_VERSION || '1.0.0';

// Transition duration (ms)
const TRANSITION_DURATION = 225;

// Main navigation
const mainNavItems: NavItem[] = [
  { text: 'Home', icon: <HomeIcon />, path: '/' },
  { text: 'My Teams', icon: <GroupsIcon />, path: '/teams' },
  { text: 'Newsletter', icon: <ArticleIcon />, path: '/newsletter', adminOnly: true },
  { text: 'Documentation', icon: <DocsIcon />, path: '/docs' },
];

// Admin sidebar entry (single item — tabs live inside AdminHubPage)
const adminNavItem: NavItem = { text: 'Administration', icon: <AdminIcon />, path: '/admin', adminOnly: true };

export default function Layout() {
  const theme = useTheme();
  const { mode, toggleTheme } = useThemeMode();
  const isDark = mode === 'dark';
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // Mobile: temporary drawer (overlay), always starts closed
  const [mobileOpen, setMobileOpen] = useState(false);

  // Desktop: persistent drawer (push), remembers state in localStorage
  const [desktopOpen, setDesktopOpen] = useState(() => {
    const saved = localStorage.getItem(SIDEBAR_STORAGE_KEY);
    return saved === null ? true : saved === 'true';
  });

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // Detect team context from URL
  const teamMatch = useMatch('/teams/:teamId/*');
  const activeTeamId = teamMatch?.params?.teamId;

  // Fetch team name when in team context
  const { data: activeTeam } = useQuery({
    queryKey: ['team', activeTeamId],
    queryFn: () => teamApi.get(activeTeamId!).then((r) => r.data),
    enabled: !!activeTeamId,
  });

  const handleDrawerToggle = () => {
    if (isMobile) {
      setMobileOpen((prev) => !prev);
    } else {
      setDesktopOpen((prev) => {
        const next = !prev;
        localStorage.setItem(SIDEBAR_STORAGE_KEY, String(next));
        return next;
      });
    }
  };

  const handleUserMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleUserMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    handleUserMenuClose();
    logout();
    navigate('/login');
  };

  const isActive = (path: string) => {
    if (path === '/') return location.pathname === '/';
    return location.pathname.startsWith(path);
  };

  // ── Sidebar styles (theme-aware) ────────────────────────────────
  const sidebarPaperSx = {
    boxSizing: 'border-box' as const,
    width: drawerWidth,
    ...(isDark && {
      background: 'linear-gradient(180deg, #131629 0%, #1E1B4B 100%)',
      borderRight: '1px solid rgba(255, 255, 255, 0.06)',
    }),
  };

  const navItemSx = (active: boolean) =>
    isDark
      ? {
          py: 1,
          borderRadius: 1,
          color: active ? '#fff' : 'rgba(255,255,255,0.7)',
          backgroundColor: active ? 'rgba(255,255,255,0.12)' : 'transparent',
          '&:hover': {
            backgroundColor: active ? 'rgba(255,255,255,0.16)' : 'rgba(255,255,255,0.08)',
          },
          '&.Mui-selected': {
            backgroundColor: 'rgba(255,255,255,0.12)',
            '&:hover': { backgroundColor: 'rgba(255,255,255,0.16)' },
          },
        }
      : { py: 1, borderRadius: 1 };

  const navIconSx = (active: boolean) =>
    isDark
      ? { minWidth: 36, color: active ? '#fff' : 'rgba(255,255,255,0.5)' }
      : { minWidth: 36, color: active ? 'primary.main' : 'text.secondary' };

  const renderNavItem = (item: NavItem) => {
    const active = isActive(item.path);
    return (
      <ListItem key={item.text + item.path} disablePadding sx={{ mb: 0.5 }}>
        <ListItemButton
          selected={active}
          onClick={() => {
            navigate(item.path);
            if (isMobile) setMobileOpen(false);
          }}
          sx={navItemSx(active)}
        >
          <ListItemIcon sx={navIconSx(active)}>
            {item.icon}
          </ListItemIcon>
          <ListItemText
            primary={item.text}
            primaryTypographyProps={{
              fontWeight: active ? 600 : 400,
              fontSize: '0.875rem',
            }}
          />
        </ListItemButton>
      </ListItem>
    );
  };

  // Determine AppBar title
  const getPageTitle = () => {
    if (activeTeam) {
      return activeTeam.name;
    }
    const allNavItems = [...mainNavItems, adminNavItem];
    const match = allNavItems.find(item => isActive(item.path));
    return match?.text || 'AI Wingman';
  };

  const drawer = (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Box sx={{ p: 2.5 }}>
        <Logo size={40} showText sx={isDark ? { '& span': { color: '#fff' } } : undefined} />
      </Box>
      <Box sx={{ px: 1, flex: 1, overflowY: 'auto' }}>
        <List sx={{ py: 0 }}>
          {mainNavItems.filter((item) => !item.adminOnly || user?.isAdmin).map(renderNavItem)}
        </List>
      </Box>

      {/* Bottom-pinned section */}
      <Box sx={{ borderTop: 1, borderColor: isDark ? 'rgba(255,255,255,0.12)' : 'divider' }}>
        {user?.isAdmin && (
          <Box sx={{ px: 1, pt: 1 }}>
            <List sx={{ py: 0 }}>
              {renderNavItem(adminNavItem)}
            </List>
          </Box>
        )}
        <Box
          sx={{ p: 1.5, cursor: 'pointer' }}
          onClick={() => navigate('/changelog')}
        >
          <Typography
            variant="caption"
            display="block"
            textAlign="center"
            sx={{ color: isDark ? 'rgba(255,255,255,0.4)' : 'text.secondary' }}
          >
            v{APP_VERSION}
          </Typography>
        </Box>
      </Box>
    </Box>
  );

  // Shared transition for AppBar and main content
  const transitionStyles = {
    transition: theme.transitions.create(['margin', 'width'], {
      easing: theme.transitions.easing.sharp,
      duration: TRANSITION_DURATION,
    }),
  };

  return (
    <Box sx={{ display: 'flex' }}>
      <AppBar
        position="fixed"
        sx={{
          ...transitionStyles,
          ...(desktopOpen && !isMobile && {
            width: `calc(100% - ${drawerWidth}px)`,
            ml: `${drawerWidth}px`,
          }),
        }}
      >
        <Toolbar>
          <IconButton
            aria-label={desktopOpen || mobileOpen ? 'Close navigation menu' : 'Open navigation menu'}
            aria-expanded={isMobile ? mobileOpen : desktopOpen}
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 2 }}
          >
            <MenuIcon />
          </IconButton>
          <Typography variant="h6" noWrap component="div" sx={{ flexGrow: 1, fontWeight: 600 }}>
            {getPageTitle()}
          </Typography>

          {user?.isAdmin && (
            <Chip
              label="Admin"
              size="small"
              sx={{
                mr: 2,
                backgroundColor: isDark ? 'rgba(129, 140, 248, 0.15)' : '#EEF2FF',
                color: isDark ? '#A5B4FC' : '#6366F1',
                fontWeight: 500,
              }}
            />
          )}

          <IconButton onClick={handleUserMenuOpen} sx={{ p: 0 }}>
            <Avatar
              sx={{
                bgcolor: 'transparent',
                background: 'linear-gradient(135deg, #6366F1 0%, #8B5CF6 100%)',
                fontWeight: 600,
              }}
            >
              {user?.displayName?.[0]?.toUpperCase() || user?.email?.[0]?.toUpperCase()}
            </Avatar>
          </IconButton>
          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleUserMenuClose}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'right',
            }}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'right',
            }}
          >
            <MenuItem disabled>
              <Typography variant="body2">{user?.email}</Typography>
            </MenuItem>
            <MenuItem disabled>
              <Chip label={user?.role} size="small" />
            </MenuItem>
            <Divider />
            <MenuItem onClick={() => { toggleTheme(); handleUserMenuClose(); }}>
              <ListItemIcon>
                {isDark ? <LightModeIcon fontSize="small" /> : <DarkModeIcon fontSize="small" />}
              </ListItemIcon>
              {isDark ? 'Light Mode' : 'Dark Mode'}
            </MenuItem>
            <MenuItem onClick={handleLogout}>
              <ListItemIcon>
                <LogoutIcon fontSize="small" />
              </ListItemIcon>
              Logout
            </MenuItem>
          </Menu>
        </Toolbar>
      </AppBar>

      {/* Mobile: temporary drawer (overlay) */}
      <Drawer
        variant="temporary"
        open={mobileOpen}
        onClose={handleDrawerToggle}
        ModalProps={{ keepMounted: true }}
        sx={{
          display: { xs: 'block', sm: 'none' },
          '& .MuiDrawer-paper': sidebarPaperSx,
        }}
      >
        {drawer}
      </Drawer>

      {/* Desktop: persistent drawer (push content) */}
      <Drawer
        variant="persistent"
        anchor="left"
        open={desktopOpen}
        sx={{
          display: { xs: 'none', sm: 'block' },
          '& .MuiDrawer-paper': {
            ...sidebarPaperSx,
            transition: theme.transitions.create('transform', {
              easing: theme.transitions.easing.sharp,
              duration: TRANSITION_DURATION,
            }),
          },
        }}
      >
        {drawer}
      </Drawer>

      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: 3,
          mt: '64px',
          minHeight: 'calc(100vh - 64px)',
          backgroundColor: 'background.default',
          ...transitionStyles,
          ...(desktopOpen && !isMobile && {
            ml: `${drawerWidth}px`,
          }),
        }}
      >
        <Outlet />
      </Box>

      <FeedbackFab />
    </Box>
  );
}
